import * as React from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

interface Label {
  value: string;
  label: string;
  bgcolor: string;
}

interface LabelsDropdownProps {
  onLabelsChange?: (selectedLabels: string[]) => void;
  multiSelect?: boolean;
  placeholder?: string;
  labels: any[];
  value?: string[];
  align?: "center" | "start" | "end";
  searchPlaceholder?: string;
  className?: string;
}

export function LabelsDropdown({
  onLabelsChange,
  multiSelect = true,
  placeholder = "Filter by labels...",
  labels = [],
  searchPlaceholder = "Search labels...",
  value = [],
  align = "center",
}: LabelsDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [selectedLabels, setSelectedLabels] = React.useState<string[]>(value);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Sync internal state with external value prop when it changes
  React.useEffect(() => {
    setSelectedLabels(value);
  }, [value]);

  // Ensure we only have valid label values in selectedLabels
  React.useEffect(() => {
    const validLabelValues = labels.map((l) => l.value);
    const validSelectedLabels = selectedLabels.filter((value) =>
      validLabelValues.includes(value)
    );

    if (validSelectedLabels.length !== selectedLabels.length) {
      setSelectedLabels(validSelectedLabels);
      onLabelsChange?.(validSelectedLabels);
    }
  }, [labels, selectedLabels, onLabelsChange]);

  const handleSelect = (value: string) => {
    let newSelectedLabels: string[];

    if (multiSelect) {
      // For multi-select mode
      if (selectedLabels.includes(value)) {
        newSelectedLabels = selectedLabels.filter((label) => label !== value);
      } else {
        newSelectedLabels = [...selectedLabels, value];
      }
    } else {
      // For single-select mode
      newSelectedLabels = selectedLabels.includes(value) ? [] : [value];
      setOpen(false);
    }

    setSelectedLabels(newSelectedLabels);
    onLabelsChange?.(newSelectedLabels);
  };

  const clearLabels = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLabels([]);
    onLabelsChange?.([]);
  };

  // Filter labels based on search query
  const filteredLabels = labels.filter((label) =>
    label?.label?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
  );

  // Get valid selected labels that exist in the labels array
  const validSelectedLabels = selectedLabels.filter((value) =>
    labels.some((label) => label.value === value)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-7 transition-all duration-200",
            open && "ring-2 ring-primary/20 border-primary",
            validSelectedLabels.length > 0 && "bg-primary/5 border-primary/30"
          )}
        >
          {validSelectedLabels.length > 0 ? (
            <div className="flex flex-1 flex-wrap gap-1.5 pr-2">
              {multiSelect ? (
                <div className="flex items-center group relative">
                  <div className="flex items-center gap-1.5 max-w-[150px] overflow-hidden">
                    {validSelectedLabels.slice(0, 2).map((labelValue, index) => {
                      const label = labels.find((l) => l.value === labelValue);
                      if (!label || !label.label) return null;

                      return (
                        <div
                          key={labelValue}
                          className="px-1.5 py-0.5 rounded text-xs font-medium flex items-center bg-muted text-muted-foreground shadow-sm transition-all duration-200 hover:scale-105"
                          style={{
                            maxWidth: "70px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span className="truncate">{label.label}</span>
                        </div>
                      );
                    })}
                    {validSelectedLabels.length > 2 && (
                      <span className="text-xs px-1.5 py-0.5 bg-muted/50 border rounded text-muted-foreground font-medium">
                        +{validSelectedLabels.length - 2}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 p-0 hover:bg-muted/80 ml-1 transition-colors duration-200"
                    onClick={clearLabels}
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Clear</span>
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {(() => {
                    const label = labels.find(
                      (l) => l.value === validSelectedLabels[0]
                    );
                    if (!label || !label.label)
                      return (
                        <span className="text-muted-foreground">
                          {placeholder}
                        </span>
                      );

                    return (
                      <div
                        className="px-2 py-1 rounded-md text-xs font-medium shadow-sm bg-muted text-muted-foreground"
                      >
                        <span className="truncate">{label.label}</span>
                      </div>
                    );
                  })()}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 p-0 hover:bg-muted/80 transition-colors duration-200"
                    onClick={clearLabels}
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Clear</span>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className={cn(
            "ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform duration-200",
            open && "rotate-180"
          )} />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[var(--radix-popover-trigger-width)] p-0 border-0 shadow-xl" 
        align={align}
        sideOffset={4}
      >
        <div className="space-y-0">
          {/* Enhanced Search Header */}
          <div className="sticky top-0 bg-background border-b p-1">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-3.5 h-3.5" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 pr-2 h-7 border-0 focus-visible:ring-2 focus-visible:ring-primary/20 bg-transparent text-sm w-full"
                autoFocus
              />
            </div>
          </div>

          {/* Labels List */}
          <div className="max-h-[90px] overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
            {filteredLabels.length === 0 ? (
              <div className="py-6 text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-muted/50 flex items-center justify-center">
                  <Search className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground font-medium">No labels found</p>
                <p className="text-xs text-muted-foreground mt-1">Try adjusting your search</p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredLabels.map((label) => (
                  <div
                    key={label.value}
                    className={cn(
                      "flex items-center rounded-md px-1.5 py-1 text-sm cursor-pointer transition-all duration-200",
                      "bg-accent/80 hover:scale-[1.01] active:scale-[0.99]",
                      "border hover:shadow-sm border-accent/30",
                      selectedLabels.includes(label.value) && "bg-primary/10 border-primary/30"
                    )}
                    onClick={() => handleSelect(label.value)}
                  >
                    {selectedLabels.includes(label.value) && (
                    <div className="mr-2 flex h-4 w-4 items-center justify-center">
                      <Check
                        className={cn(
                          "h-4 w-4 transition-all duration-200",
                          selectedLabels.includes(label.value)
                            ? "opacity-100 scale-100 text-primary"
                            : "opacity-0 scale-75"
                        )}
                      />
                    </div>
                    )}
                    <div
                      className="px-1.5 py-0.5 rounded text-xs font-medium truncate transition-all duration-200 bg-muted text-muted-foreground"
                    >
                      <span className="">{label?.label ?? ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Enhanced Footer */}
          {multiSelect && selectedLabels.length > 0 && (
            <div className="sticky bottom-0 bg-background border-t p-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs justify-between h-5 hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
                onClick={clearLabels}
              >
                <span>Clear all selected ({selectedLabels.length})</span>
                <X className="h-3 w-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
